import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { api, fmtEUR, CATEGORIE_LABELS, CATEGORIE_COLORS } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i);
const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const emptyCharge = { libelle: '', categorie: 'deplacement', montant: 0, date: '', projet_id: '', description: '' };
const emptyPaiement = { projet_code: '', montant: 0, date: '', libelle: '' };

export default function Charges() {
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [moisFilter, setMoisFilter] = useState('all');
  const [categorieFilter, setCategorieFilter] = useState('all');
  const [projetFilter, setProjetFilter] = useState('all');
  const [charges, setCharges] = useState([]);
  const [paiements, setPaiements] = useState([]);
  const [projets, setProjets] = useState([]);
  const [chargeOpen, setChargeOpen] = useState(false);
  const [paiementOpen, setPaiementOpen] = useState(false);
  const [editingChargeId, setEditingChargeId] = useState(null);
  const [chargeForm, setChargeForm] = useState(emptyCharge);
  const [paiementForm, setPaiementForm] = useState(emptyPaiement);

  const load = async () => {
    const [c, p, pr] = await Promise.all([
      api.get(`/charges?annee=${annee}`),
      api.get(`/paiements?annee=${annee}`),
      api.get('/projets'),
    ]);
    setCharges(c.data);
    setPaiements(p.data);
    setProjets(pr.data);
  };

  useEffect(() => { load(); }, [annee]);

  const projMap = useMemo(() => Object.fromEntries(projets.map(p => [p.id, p])), [projets]);

  const filteredCharges = useMemo(() => {
    return charges.filter(c => {
      if (moisFilter !== 'all') {
        const m = Number(c.date.slice(5, 7));
        if (m !== Number(moisFilter)) return false;
      }
      if (categorieFilter !== 'all' && c.categorie !== categorieFilter) return false;
      if (projetFilter !== 'all') {
        if (projetFilter === '_none' && c.projet_id) return false;
        if (projetFilter !== '_none' && c.projet_id !== projetFilter) return false;
      }
      return true;
    });
  }, [charges, moisFilter, categorieFilter, projetFilter]);

  const openCreateCharge = () => {
    setEditingChargeId(null);
    setChargeForm({ ...emptyCharge, date: new Date().toISOString().slice(0, 10) });
    setChargeOpen(true);
  };

  const openEditCharge = (c) => {
    setEditingChargeId(c.id);
    setChargeForm({
      libelle: c.libelle, categorie: c.categorie, montant: c.montant,
      date: c.date, projet_id: c.projet_id || '', description: c.description || '',
    });
    setChargeOpen(true);
  };

  const submitCharge = async (e) => {
    e.preventDefault();
    const payload = {
      ...chargeForm,
      montant: Number(chargeForm.montant),
      projet_id: chargeForm.projet_id || null,
    };
    try {
      if (editingChargeId) {
        await api.put(`/charges/${editingChargeId}`, payload);
        toast.success('Charge mise à jour');
      } else {
        await api.post('/charges', payload);
        toast.success('Charge ajoutée');
      }
      setChargeOpen(false);
      setChargeForm(emptyCharge);
      setEditingChargeId(null);
      load();
    } catch (err) {
      console.error('[Charges] submit failed', err);
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map(d => `${(d.loc || []).join('.')}: ${d.msg}`).join(' | ')
        : 'Erreur';
      toast.error(msg);
    }
  };

  const submitPaiement = async (e) => {
    e.preventDefault();
    try {
      await api.post('/paiements', { ...paiementForm, montant: Number(paiementForm.montant) });
      toast.success('Paiement enregistré');
      setPaiementOpen(false);
      setPaiementForm(emptyPaiement);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const deleteCharge = async (id) => {
    if (!window.confirm('Supprimer cette charge ?')) return;
    await api.delete(`/charges/${id}`);
    toast.success('Supprimée');
    load();
  };
  const deletePaiement = async (id) => {
    if (!window.confirm('Supprimer ce paiement ?')) return;
    await api.delete(`/paiements/${id}`);
    toast.success('Supprimé');
    load();
  };

  const totalCharges = filteredCharges.reduce((s, c) => s + c.montant, 0);
  const totalPaiements = paiements.reduce((s, p) => s + p.montant, 0);

  // Synthèse par catégorie
  const byCategorie = useMemo(() => {
    const map = {};
    for (const c of filteredCharges) {
      map[c.categorie] = (map[c.categorie] || 0) + c.montant;
    }
    return Object.entries(map).sort(([, a], [, b]) => b - a);
  }, [filteredCharges]);

  return (
    <Layout
      title="Charges & Paiements"
      subtitle={`Exercice ${annee}`}
      actions={
        <Select value={String(annee)} onValueChange={(v) => setAnnee(Number(v))}>
          <SelectTrigger className="w-32 rounded-none border-zinc-900"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none">
            {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      }
    >
      <Tabs defaultValue="charges" className="w-full">
        <TabsList className="rounded-none bg-transparent border-b border-zinc-200 w-full justify-start h-auto p-0">
          <TabsTrigger value="charges" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-zinc-900 data-[state=active]:shadow-none px-6 py-3" data-testid="tab-charges">
            Charges <span className="ml-2 font-mono text-xs text-zinc-500">{fmtEUR(totalCharges)}</span>
          </TabsTrigger>
          <TabsTrigger value="paiements" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-zinc-900 data-[state=active]:shadow-none px-6 py-3" data-testid="tab-paiements">
            Paiements honoraires <span className="ml-2 font-mono text-xs text-zinc-500">{fmtEUR(totalPaiements)}</span>
          </TabsTrigger>
        </TabsList>

        {/* ──────────────── CHARGES ──────────────── */}
        <TabsContent value="charges" className="mt-6">
          {/* Filtres + bouton */}
          <div className="bg-white border border-zinc-200 p-4 mb-4 flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">Mois</Label>
              <Select value={moisFilter} onValueChange={setMoisFilter}>
                <SelectTrigger className="w-32 rounded-none" data-testid="charges-mois-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">Tous</SelectItem>
                  {months.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">Catégorie</Label>
              <Select value={categorieFilter} onValueChange={setCategorieFilter}>
                <SelectTrigger className="w-48 rounded-none" data-testid="charges-cat-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">Toutes</SelectItem>
                  {Object.entries(CATEGORIE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">Projet</Label>
              <Select value={projetFilter} onValueChange={setProjetFilter}>
                <SelectTrigger className="w-56 rounded-none" data-testid="charges-projet-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none max-h-80">
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="_none">— Charges globales (sans projet) —</SelectItem>
                  {projets.map(p => <SelectItem key={p.id} value={p.id}>{p.code} — {p.nom}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="ml-auto">
              <Sheet open={chargeOpen} onOpenChange={setChargeOpen}>
                <SheetTrigger asChild>
                  <Button className="rounded-none bg-[#002FA7] hover:bg-[#001B63]" onClick={openCreateCharge} data-testid="nouvelle-charge-btn">
                    <Plus size={14} className="mr-2" /> Nouvelle charge
                  </Button>
                </SheetTrigger>
                <SheetContent className="rounded-none w-full sm:max-w-md overflow-y-auto">
                  <SheetHeader><SheetTitle className="font-display font-bold text-2xl tracking-tight">{editingChargeId ? 'Modifier la charge' : 'Nouvelle charge'}</SheetTitle></SheetHeader>
                  <form onSubmit={submitCharge} className="space-y-4 mt-6">
                    <div>
                      <Label>Libellé *</Label>
                      <Input required className="rounded-none" value={chargeForm.libelle} onChange={(e) => setChargeForm({ ...chargeForm, libelle: e.target.value })} placeholder="Ex: Déplacement chantier Blois" data-testid="charge-libelle-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Catégorie *</Label>
                        <Select value={chargeForm.categorie} onValueChange={(v) => setChargeForm({ ...chargeForm, categorie: v })}>
                          <SelectTrigger className="rounded-none" data-testid="charge-cat-select"><SelectValue /></SelectTrigger>
                          <SelectContent className="rounded-none">
                            {Object.entries(CATEGORIE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Date *</Label>
                        <Input required type="date" className="rounded-none font-mono" value={chargeForm.date} onChange={(e) => setChargeForm({ ...chargeForm, date: e.target.value })} data-testid="charge-date-input" />
                      </div>
                    </div>
                    <div>
                      <Label>Montant TTC (€) *</Label>
                      <Input required type="number" step="0.01" className="rounded-none font-mono" value={chargeForm.montant} onChange={(e) => setChargeForm({ ...chargeForm, montant: e.target.value })} data-testid="charge-montant-input" />
                    </div>
                    <div>
                      <Label>Projet rattaché (optionnel)</Label>
                      <Select value={chargeForm.projet_id || '_none'} onValueChange={(v) => setChargeForm({ ...chargeForm, projet_id: v === '_none' ? '' : v })}>
                        <SelectTrigger className="rounded-none" data-testid="charge-projet-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="rounded-none max-h-80">
                          <SelectItem value="_none">— Charge globale —</SelectItem>
                          {projets.map(p => <SelectItem key={p.id} value={p.id}>{p.code} — {p.nom}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-zinc-500 mt-1">Rattacher à un projet améliore l&apos;analyse de rentabilité.</p>
                    </div>
                    <div>
                      <Label>Description / Justificatif</Label>
                      <Input className="rounded-none" value={chargeForm.description} onChange={(e) => setChargeForm({ ...chargeForm, description: e.target.value })} placeholder="N° facture, fournisseur..." />
                    </div>
                    <Button type="submit" className="w-full rounded-none bg-[#002FA7] hover:bg-[#001B63]" data-testid="charge-submit-btn">
                      {editingChargeId ? 'Mettre à jour' : 'Enregistrer'}
                    </Button>
                  </form>
                </SheetContent>
              </Sheet>
            </div>
          </div>

          {/* Synthèse par catégorie */}
          {byCategorie.length > 0 && (
            <div className="bg-white border border-zinc-200 mb-4">
              <div className="border-b border-zinc-900 px-4 py-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-medium">Synthèse par catégorie ({fmtEUR(totalCharges)})</div>
              </div>
              <div className="p-3 flex flex-wrap gap-2">
                {byCategorie.map(([cat, sum]) => (
                  <div key={cat} className={`border px-3 py-1.5 ${CATEGORIE_COLORS[cat] || 'border-zinc-300'}`}>
                    <div className="text-[10px] uppercase tracking-wider">{CATEGORIE_LABELS[cat] || cat}</div>
                    <div className="font-mono font-medium text-sm">{fmtEUR(sum)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white border border-zinc-200" data-testid="charges-table">
            <table className="w-full text-sm">
              <thead className="border-b border-zinc-900">
                <tr className="text-left">
                  <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Date</th>
                  <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Libellé</th>
                  <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Catégorie</th>
                  <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Projet</th>
                  <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">Montant</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filteredCharges.map((c) => {
                  const proj = c.projet_id ? projMap[c.projet_id] : null;
                  return (
                    <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">{c.date}</td>
                      <td className="px-4 py-2 font-medium">
                        {c.libelle}
                        {c.description && <div className="text-[10px] text-zinc-500">{c.description}</div>}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        <span className={`inline-block px-2 py-0.5 border uppercase tracking-wider text-[10px] ${CATEGORIE_COLORS[c.categorie] || 'border-zinc-200 bg-zinc-100'}`}>
                          {CATEGORIE_LABELS[c.categorie]}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {proj ? <span className="font-mono">{proj.code}</span> : <span className="text-zinc-400">—</span>}
                      </td>
                      <td className="px-4 py-2 font-mono text-right text-rose-600 font-medium">{fmtEUR(c.montant)}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        <button onClick={() => openEditCharge(c)} className="p-1 hover:bg-zinc-200" data-testid={`edit-charge-${c.id}`}><Pencil size={12} /></button>
                        <button onClick={() => deleteCharge(c.id)} className="p-1 hover:bg-rose-100 text-rose-600 ml-1"><Trash2 size={12} /></button>
                      </td>
                    </tr>
                  );
                })}
                {filteredCharges.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-zinc-500">Aucune charge.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ──────────────── PAIEMENTS ──────────────── */}
        <TabsContent value="paiements" className="mt-6">
          <div className="flex justify-end mb-4">
            <Sheet open={paiementOpen} onOpenChange={setPaiementOpen}>
              <SheetTrigger asChild>
                <Button className="rounded-none bg-[#002FA7] hover:bg-[#001B63]" data-testid="nouveau-paiement-btn">
                  <Plus size={14} className="mr-2" /> Nouveau paiement
                </Button>
              </SheetTrigger>
              <SheetContent className="rounded-none w-full sm:max-w-md">
                <SheetHeader><SheetTitle className="font-display font-bold text-2xl tracking-tight">Paiement d&apos;honoraires</SheetTitle></SheetHeader>
                <form onSubmit={submitPaiement} className="space-y-4 mt-6">
                  <div>
                    <Label>Projet *</Label>
                    <Select value={paiementForm.projet_code} onValueChange={(v) => setPaiementForm({ ...paiementForm, projet_code: v })}>
                      <SelectTrigger className="rounded-none"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                      <SelectContent className="rounded-none max-h-80">
                        {projets.map((p) => <SelectItem key={p.code} value={p.code}><span className="font-mono text-xs">{p.code}</span> — {p.nom}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Date *</Label>
                    <Input required type="date" className="rounded-none font-mono" value={paiementForm.date} onChange={(e) => setPaiementForm({ ...paiementForm, date: e.target.value })} />
                  </div>
                  <div>
                    <Label>Montant (€) *</Label>
                    <Input required type="number" step="0.01" className="rounded-none font-mono" value={paiementForm.montant} onChange={(e) => setPaiementForm({ ...paiementForm, montant: e.target.value })} />
                  </div>
                  <div>
                    <Label>Libellé</Label>
                    <Input className="rounded-none" value={paiementForm.libelle} onChange={(e) => setPaiementForm({ ...paiementForm, libelle: e.target.value })} placeholder="Acompte, solde..." />
                  </div>
                  <Button type="submit" className="w-full rounded-none bg-[#002FA7] hover:bg-[#001B63]">Enregistrer</Button>
                </form>
              </SheetContent>
            </Sheet>
          </div>

          <div className="bg-white border border-zinc-200" data-testid="paiements-table">
            <table className="w-full text-sm">
              <thead className="border-b border-zinc-900">
                <tr className="text-left">
                  <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Date</th>
                  <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Projet</th>
                  <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Libellé</th>
                  <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">Montant</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {paiements.map((p) => (
                  <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-4 py-2 font-mono text-xs">{p.date}</td>
                    <td className="px-4 py-2 font-mono text-xs">{p.projet_code}</td>
                    <td className="px-4 py-2 text-zinc-600">{p.libelle || '—'}</td>
                    <td className="px-4 py-2 font-mono text-right text-emerald-700 font-medium">{fmtEUR(p.montant)}</td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => deletePaiement(p.id)} className="p-1 hover:bg-rose-100 text-rose-600"><Trash2 size={12} /></button>
                    </td>
                  </tr>
                ))}
                {paiements.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-zinc-500">Aucun paiement.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
